import React, { useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal, Placeholder } from "@/components/primitives";
import { Icon } from "@/components/icons";
import { trackEvent } from "@/lib/api";

const TOPICS = [
  { icon: "Lock", title: "Encryption", desc: "Data in transit and at rest protected with strong cryptographic protocols." },
  { icon: "Key", title: "Access Controls", desc: "Role-based access control and least-privilege principles across systems." },
  { icon: "ShieldCheck", title: "API Security", desc: "Scoped keys, rate limiting, and monitored API surfaces." },
  { icon: "Wallet", title: "Wallet Security", desc: "Segregation and controls around supported wallet infrastructure." },
  { icon: "Circuitry", title: "Smart-Contract Security", desc: "Careful review of on-chain interaction surfaces where applicable." },
  { icon: "Eye", title: "Monitoring", desc: "Continuous observability across engine and infrastructure." },
  { icon: "Broadcast", title: "Anomaly Detection", desc: "Real-time detection of unusual behavior and execution conditions." },
  { icon: "Gauge", title: "Fail-Safe Execution", desc: "Execution constraints and circuit-breaker style safeguards." },
  { icon: "Database", title: "Infrastructure Redundancy", desc: "Redundancy to reduce single points of failure." },
  { icon: "Fingerprint", title: "Authentication", desc: "Secure authentication flows and session handling." },
  { icon: "ClipboardText", title: "Audit Logging", desc: "System activity logged for traceability." },
];

const BADGES = ["Security Audit", "Smart Contract Audit", "Independent Verification"];

export default function Security() {
  useEffect(() => {
    trackEvent("page_view", { page: "security" });
  }, []);
  return (
    <div>
      <PageHeader
        eyebrow="Security"
        title="Security engineered into the infrastructure."
        sub="Security is foundational to how Q-Core is built and operated. Below is an overview of our security posture. We only display audit badges once they are independently verified."
      />

      <Section grid>
        <SectionHeading eyebrow="Posture" title="Defense across the stack." className="mb-12" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOPICS.map((t, i) => (
            <Reveal key={t.title} delay={(i % 3) * 0.05}>
              <div className="qs-beam h-full rounded-xl border border-white/10 bg-[#0A0B10] p-6">
                <span className="grid place-items-center h-10 w-10 rounded-lg border border-white/10 bg-white/[0.03] text-[#2DD4BF] mb-4">
                  <Icon name={t.icon} size={20} weight="duotone" />
                </span>
                <h3 className="text-base font-bold text-white mb-1.5">{t.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Verification"
          title="Badges activate only when verified."
          sub="We do not claim audits that have not occurred. These placeholders activate once independently verified."
          className="mb-10"
        />
        <div className="grid sm:grid-cols-3 gap-5" data-testid="audit-badges">
          {BADGES.map((b) => (
            <div key={b} className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-7 text-center">
              <Icon name="ShieldCheck" size={30} weight="duotone" className="text-[#64748B] mx-auto mb-4" />
              <p className="text-white font-semibold">{b}</p>
              <p className="mt-3"><Placeholder>[INSERT VERIFIED AUDIT]</Placeholder></p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#64748B]">Pending Verification</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
